import React from "react";
import spacetime from "spacetime";
import { useTranslation } from "react-i18next";

export default function AirportHeader({ airport }) {
  const { t } = useTranslation();

  const windLabel =
    airport.wind_direction === 0
      ? t("airport.variableWinds")
      : `${airport.wind_direction}°`;

  return (
    <header className="mb-10 md:mb-16 animate-fade-in">
      {/* ICAO badge + airport name */}
      <div className="text-center mb-8">
        <span className="inline-block text-aviation-cyan text-sm font-bold tracking-[0.3em] uppercase mb-3">
          {airport.icao}
        </span>
        <h1 className="text-2xl sm:text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight px-2 sm:px-0 break-words">
          {airport.name}
        </h1>
        <div className="flex items-center justify-center gap-2 text-xs text-slate-500 font-mono">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full bg-aviation-radarGreen"
            style={{ animation: "pulseSoft 3s ease-in-out infinite" }}
            aria-hidden="true"
          />
          <span>
            {t("airport.lastUpdated")}:{" "}
            <time dateTime={airport.time} className="text-slate-400">
              {spacetime(airport.time).format(
                "{year}-{iso-month}-{date-pad} {hour-24-pad}:{minute-pad}"
              )}{" "}
              UTC
            </time>
          </span>
        </div>

        {/* Action Links */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 mt-4 sm:mt-6 px-2">
          <a
            href={`https://www.flightradar24.com/airport/${airport.iata ? airport.iata.toLowerCase() : airport.icao.toLowerCase()}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 transition-colors border border-slate-700 px-4 py-2 rounded-full shadow-lg"
          >
            <img src={require("./fr24_logo.png")} alt="Flightradar24" className="h-4 object-contain" />
            <span className="text-sm font-semibold text-white">Flightradar24</span>
          </a>
          
          {airport.wikipedia_link && (
            <a
              href={airport.wikipedia_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 transition-colors border border-slate-700 px-4 py-2 rounded-full shadow-lg"
            >
              <span className="text-sm font-semibold text-white">Wikipedia</span>
            </a>
          )}
        </div>
      </div>

      {/* METAR + Wind cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 max-w-3xl mx-auto px-2 sm:px-0">
        {/* METAR card */}
        <section
          className="glass-card rounded-card p-5 shadow-card"
          aria-labelledby="metar-heading"
        >
          <h2
            id="metar-heading"
            className="text-xs font-bold text-aviation-cyan tracking-widest uppercase mb-3"
          >
            {t("airport.metar")}
          </h2>
          {airport.metar ? (
            <p
              className="font-mono text-xs text-slate-300 leading-relaxed break-all"
              style={{ animation: "dataFlash 1.5s ease-out" }}
            >
              {airport.metar}
            </p>
          ) : (
            <p className="text-xs text-slate-500 italic">{t("airport.noMetar")}</p>
          )}
        </section>

        {/* Wind card */}
        <section
          className="glass-card rounded-card p-5 shadow-card"
          aria-labelledby="wind-heading"
        >
          <h2
            id="wind-heading"
            className="text-xs font-bold text-aviation-cyan tracking-widest uppercase mb-3"
          >
            {t("airport.wind")}
          </h2>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">{t("airport.windDirection")}</span>
              <span className="font-mono font-bold text-white text-sm">
                {windLabel}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">{t("airport.windSpeed")}</span>
              <span className="font-mono font-bold text-white text-sm">
                {airport.wind_speed === 0 ? (
                  <span className="text-slate-400 font-normal text-xs">
                    {t("airport.windCalm")}
                  </span>
                ) : (
                  <>
                    {airport.wind_speed}{" "}
                    <span className="text-xs font-normal text-slate-400">
                      {t("wind.kts")}
                    </span>
                  </>
                )}
              </span>
            </div>

            {/* Mini wind compass */}
            {airport.wind_direction !== 0 && (
              <div
                className="mt-3 flex items-center justify-center"
                aria-hidden="true"
              >
                <div className="relative w-10 h-10 border border-aviation-cyan/20 rounded-full flex items-center justify-center">
                  <div
                    className="absolute w-0.5 h-4 bg-aviation-cyan rounded-full"
                    style={{
                      bottom: "50%",
                      left: "calc(50% - 1px)",
                      transform: `rotate(${airport.wind_direction}deg)`,
                      transformOrigin: "50% 100%",
                    }}
                  />
                  <span className="absolute top-0.5 left-1/2 -translate-x-1/2 text-[8px] text-slate-600 font-bold">
                    {t("wind.compassNorth")}
                  </span>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </header>
  );
}
